import React, { useState, useEffect, useRef } from 'react';
import { Check, X, PencilSimple } from '@phosphor-icons/react';

export interface EditableTextFieldProps {
  icon: React.ComponentType<any>;
  label: string;
  field: string;
  value: any;
  isLoading?: boolean;
  isRequired?: boolean;
  isProminentDescription?: boolean;
  isTextarea?: boolean;
  onSave: (field: string, value: any) => void;
  isSaving?: boolean;
  showSuccess?: boolean;
  validationError?: string;
}

export const EditableTextField: React.FC<EditableTextFieldProps> = ({
  icon: Icon,
  label,
  field,
  value,
  isLoading = false,
  isRequired = false,
  isProminentDescription = false,
  isTextarea = false,
  onSave,
  isSaving = false,
  showSuccess = false,
  validationError
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(() => {
    const initialValue = value === null || value === undefined ? '' : value;
    console.log(`🔧 TextField ${field} initialized with:`, initialValue);
    return initialValue;
  });
  
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Update local value when prop value changes (but not during editing)
  useEffect(() => {
    if (!isEditing) {
      const newValue = value === null || value === undefined ? '' : value;
      setLocalValue(newValue);
      console.log(`📝 TextField ${field} updated to:`, newValue);
    }
  }, [value, isEditing, field]);
  
  const startEditing = () => {
    const editValue = value === null || value === undefined ? '' : value;
    setLocalValue(editValue);
    setIsEditing(true);
    console.log(`✏️ Started editing ${field} with value:`, editValue);
  };
  
  const cancelEditing = () => {
    console.log(`❌ Cancelled editing ${field}`);
    setLocalValue(value || '');
    setIsEditing(false);
  };
  
  const handleSave = () => {
    console.log(`💾 Saving ${field} with value:`, localValue);
    const valueToSave = isTextarea 
      ? (textareaRef.current?.value ?? localValue ?? '')
      : (inputRef.current?.value ?? localValue ?? '');
    
    onSave(field, valueToSave);
    setIsEditing(false);
  };
  
  const containerClasses = isProminentDescription 
    ? "p-4 border-2 border-moss-primary/30 bg-moss-primary/5 rounded-lg mb-4" 
    : "p-3 border-b border-neutral-primary/10 last:border-b-0";
    
  if (!isEditing) {
    return (
      <div className={containerClasses}>
        <div className="flex items-center gap-2 mb-2">
          <Icon size={isProminentDescription ? 20 : 18} className="text-neutral-primary/60" />
          <span className={`font-medium text-neutral-primary ${isProminentDescription ? 'text-lg' : 'text-sm'}`}>
            {label}
            {isRequired && <span className="text-red-500 ml-1">*</span>}
          </span>
          {isProminentDescription && (
            <span className="text-xs text-moss-primary bg-moss-primary/10 px-2 py-1 rounded-full">
              Belangrijke publieke info
            </span>
          )}
          <button
            onClick={startEditing}
            className="ml-auto text-neutral-primary/40 hover:text-moss-primary transition-colors"
            disabled={isLoading}
          >
            <PencilSimple size={16} />
          </button>
        </div>
        
        {validationError && (
          <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded-sm text-xs text-red-700">
            <strong>Fout:</strong> {validationError}
          </div>
        )}
        
        <div className={`min-h-[40px] flex items-center justify-between ${isProminentDescription ? 'pl-0' : 'pl-6'}`}>
          <div className="flex-1">
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-moss-primary border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm text-neutral-primary/60">Laden...</span>
              </div>
            ) : (
              <p className={`text-neutral-primary/80 whitespace-pre-wrap ${isProminentDescription ? 'text-base leading-relaxed' : 'text-sm'}`}>
                {value || (isProminentDescription ? 'Geen commerciële beschrijving toegevoegd. Dit is cruciaal voor potentiële huurders!' : 'Geen beschrijving toegevoegd...')}
              </p>
            )}
          </div>
          {showSuccess && (
            <div className="flex items-center gap-1 px-2 py-1 bg-status-good text-white rounded-sm text-xs animate-pulse">
              <Check size={12} weight="bold" />
              Opgeslagen!
            </div>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className={containerClasses}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={isProminentDescription ? 20 : 18} className="text-neutral-primary/60" />
        <span className={`font-medium text-neutral-primary ${isProminentDescription ? 'text-lg' : 'text-sm'}`}>
          {label}
          {isRequired && <span className="text-red-500 ml-1">*</span>}
        </span>
      </div>
      
      {validationError && (
        <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded-sm text-xs text-red-700">
          <strong>Fout:</strong> {validationError}
        </div>
      )}
      
      <div className={isProminentDescription ? 'pl-0' : 'pl-6'}>
        {isTextarea ? (
          <textarea
            ref={textareaRef}
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            className={`w-full border rounded px-3 py-2 focus:outline-hidden resize-none ${
              validationError 
                ? 'border-red-300 focus:border-red-500' 
                : 'border-neutral-primary/20 focus:border-moss-primary'
            } ${isProminentDescription ? 'text-base min-h-[120px]' : 'text-sm'}`}
            rows={isProminentDescription ? 6 : 3}
            placeholder={isProminentDescription 
              ? "Beschrijf wat deze ruimte bijzonder maakt. Denk aan: ligging, voorzieningen, sfeer, unieke kenmerken..." 
              : "Voer beschrijving in..."}
            autoFocus
          />
        ) : (
          <input
            ref={inputRef}
            type="text"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            className={`w-full border rounded px-3 py-2 focus:outline-hidden ${
              validationError 
                ? 'border-red-300 focus:border-red-500' 
                : 'border-neutral-primary/20 focus:border-moss-primary'
            } text-sm`}
            placeholder="Voer tekst in..."
            autoFocus
          />
        )}
        
        <div className="flex justify-end gap-2 mt-2">
          <button
            type="button"
            disabled={isSaving}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSave();
            }}
            className={`flex items-center gap-1 px-3 py-1 rounded text-sm transition-colors ${
              isSaving 
                ? 'bg-gray-400 text-white cursor-not-allowed' 
                : 'bg-moss-primary text-white hover:bg-moss-primary/90'
            }`}
          >
            {isSaving ? (
              <>
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Bewaren...
              </>
            ) : (
              <>
                <Check size={14} weight="bold" />
                Bewaren
              </>
            )}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              cancelEditing();
            }}
            className="flex items-center gap-1 px-3 py-1 bg-neutral-primary/10 text-neutral-primary rounded-sm text-sm hover:bg-neutral-primary/20 transition-colors"
          >
            <X size={14} weight="bold" />
            Annuleren
          </button>
        </div>
      </div>
    </div>
  );
};
